import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, errorId: null };
  }

  static getDerivedStateFromError(error) {
    const errorId = `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
    return { hasError: true, error, errorId };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    const report = {
      error_id: this.state.errorId,
      message: error?.message || 'Unknown error',
      stack: error?.stack || '',
      component_stack: errorInfo?.componentStack || '',
      route: window.location.pathname,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };

    console.error('[ErrorBoundary] Caught error:', report);

    // Report to backend
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (backendUrl) {
      fetch(`${backendUrl}/api/system/frontend-errors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(report),
      }).catch(() => {}); // fire-and-forget
    }
  }

  handleReload = () => {
    window.location.href = '/login';
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div data-testid="error-boundary-screen" style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0a0a0f',
          padding: '24px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '520px',
            background: '#12121a',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.08)',
            padding: '40px 32px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              margin: '0 auto 20px',
              background: 'rgba(239,68,68,0.12)',
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px'
            }}>
              !
            </div>
            <h1 data-testid="error-boundary-title" style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#f1f1f4',
              marginBottom: '8px'
            }}>
              Something went wrong
            </h1>
            <p style={{
              color: '#8a8a9a',
              fontSize: '0.875rem',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              The application encountered an unexpected error. This has been logged automatically.
            </p>

            {/* Error details */}
            <div data-testid="error-boundary-details" style={{
              background: '#0a0a0f',
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.06)',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'left',
              maxHeight: '200px',
              overflow: 'auto'
            }}>
              <div style={{ color: '#6a6a7a', fontSize: '0.7rem', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Error ID: <span style={{ color: '#ef4444' }}>{this.state.errorId}</span>
              </div>
              <div style={{ color: '#6a6a7a', fontSize: '0.7rem', marginBottom: '6px' }}>
                Route: <span style={{ color: '#a0a0b0' }}>{window.location.pathname}</span>
              </div>
              <div style={{
                color: '#ef4444',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                wordBreak: 'break-word',
                marginTop: '8px'
              }}>
                {this.state.error?.message || 'Unknown error'}
              </div>
              {this.state.errorInfo?.componentStack && (
                <pre style={{
                  color: '#5a5a6a',
                  fontSize: '0.65rem',
                  fontFamily: 'monospace',
                  marginTop: '8px',
                  whiteSpace: 'pre-wrap',
                  maxHeight: '100px',
                  overflow: 'auto'
                }}>
                  {this.state.errorInfo.componentStack.slice(0, 500)}
                </pre>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                data-testid="error-boundary-retry-btn"
                onClick={this.handleRetry}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '10px',
                  color: '#c0c0d0',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Try Again
              </button>
              <button
                data-testid="error-boundary-login-btn"
                onClick={this.handleReload}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#2563eb',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
